import { useLayoutEffect, useRef } from 'react';
import { animate, stagger } from 'animejs';
import { prefersReducedMotion } from '../utils/motion';

interface UseStaggerRevealOptions {
  threshold?: number;
  rootMargin?: string;
}

/**
 * When the container scrolls into view, stagger-animate `[data-stagger-item]` children.
 */
export function useStaggerReveal<T extends HTMLElement = HTMLDivElement>(
  options: UseStaggerRevealOptions = {}
) {
  const { threshold = 0.12, rootMargin = '0px 0px -48px 0px' } = options;
  const ref = useRef<T>(null);
  const playedRef = useRef(false);

  useLayoutEffect(() => {
    const container = ref.current;
    if (!container) return;

    const items = container.querySelectorAll<HTMLElement>('[data-stagger-item]');
    if (!items.length) return;

    if (prefersReducedMotion()) {
      items.forEach((el) => {
        el.style.opacity = '1';
        el.style.transform = 'none';
      });
      return;
    }

    items.forEach((el) => {
      el.style.opacity = '0';
      el.style.transform = 'translateY(28px)';
    });

    const run = () => {
      if (playedRef.current) return;
      playedRef.current = true;
      animate(items, {
        opacity: [0, 1],
        y: [28, 0],
        duration: 720,
        delay: stagger(100, { start: 0 }),
        ease: 'outCubic',
      });
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          run();
          observer.disconnect();
        }
      },
      { threshold, rootMargin }
    );

    observer.observe(container);
    return () => observer.disconnect();
  }, [threshold, rootMargin]);

  return ref;
}
